import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtDate } from "@/components/grc/shared";
import { formatBytes } from "@/components/grc/EvidenceVault";
import { uploadErrorMessage } from "@/components/grc/UploadEvidenceModal";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Download, RotateCcw, History } from "lucide-react";

const errText = (e, fallback) => {
  const d = e?.response?.data?.detail;
  return (d && typeof d === "object" ? d.message : d) || fallback;
};

export const downloadBlob = async (url, filename) => {
  const res = await api.get(url, { responseType: "blob" });
  const href = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(href);
};

export const VersionsDialog = ({ item, onClose }) => {
  const [versions, setVersions] = useState(null);
  useEffect(() => {
    if (!item) return;
    setVersions(null);
    api.get(`/grc/evidence/${item.file_id}/versions`)
      .then(({ data }) => setVersions(data.versions || []))
      .catch((e) => { toast.error(errText(e, "Could not load version history")); onClose(); });
  }, [item, onClose]);
  return (
    <Dialog open={!!item} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="evidence-versions-dialog">
        <DialogHeader>
          <DialogTitle className="text-base text-[#003366] flex items-center gap-2"><History size={16} /> Version history</DialogTitle>
          <DialogDescription className="text-xs">Every version is kept as its own file with its own SHA-256 fingerprint.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-[50vh] overflow-y-auto" data-testid="evidence-versions-list">
          {versions === null && <p className="text-xs text-slate-400">Loading…</p>}
          {(versions || []).map((v) => (
            <div key={v.evidence_id} className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2" data-testid={`evidence-version-row-${v.version_no}`}>
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${v.is_current ? "bg-[#008080] text-white" : "bg-slate-100 text-slate-600"}`}>
                v{v.version_no}{v.is_current ? " · current" : ""}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-slate-800 truncate" title={v.filename}>{v.filename}</div>
                <div className="text-[10px] text-slate-400 truncate">
                  {fmtDate(v.uploaded_at)} · {v.uploaded_by} · {formatBytes(v.size)} · sha256 {v.sha256.slice(0, 12)}…
                </div>
                {v.version_note && <div className="text-[10px] text-slate-500 italic truncate">{v.version_note}</div>}
              </div>
              {!v.deleted && (
                <Button variant="ghost" size="sm" className="h-7 px-2 text-slate-500" data-testid={`evidence-version-download-${v.version_no}`}
                  onClick={() => downloadBlob(`/grc/evidence/${v.evidence_id}/download`, v.filename).catch(() => toast.error("Download failed"))}>
                  <Download size={13} />
                </Button>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const NewVersionDialog = ({ item, onClose, onDone }) => {
  const [file, setFile] = useState(null);
  const [note, setNote] = useState("");
  const [progress, setProgress] = useState(null);
  useEffect(() => { setFile(null); setNote(""); setProgress(null); }, [item]);
  const submit = async () => {
    if (!file) return toast.error("Select a file first");
    const form = new FormData();
    form.append("file", file);
    form.append("metadata", JSON.stringify({ version_note: note || undefined }));
    setProgress(0);
    try {
      const { data } = await api.post(`/grc/evidence/${item.file_id}/versions`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => setProgress(Math.round((e.loaded / (e.total || 1)) * 100)),
      });
      toast.success(`Version ${data.version_no} uploaded`, { duration: 8000 });
      onDone();
    } catch (e) {
      toast.error(uploadErrorMessage(e));
      setProgress(null);
    }
  };
  return (
    <Dialog open={!!item} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="evidence-new-version-dialog">
        <DialogHeader>
          <DialogTitle className="text-base text-[#003366]">Upload new version</DialogTitle>
          <DialogDescription className="text-xs">
            Replaces "{item?.filename}" (v{item?.version_no || 1}). The previous version stays in the history.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-xs" data-testid="evidence-new-version-file" />
          <div className="space-y-1.5">
            <Label className="text-xs">What changed? (optional)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} className="h-9 text-xs" data-testid="evidence-new-version-note" />
          </div>
          {progress !== null && <Progress value={progress} className="h-2" data-testid="evidence-new-version-progress" />}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="evidence-new-version-cancel">Cancel</Button>
          <Button onClick={submit} disabled={progress !== null} className="bg-[#008080] hover:bg-[#1a51db]" data-testid="evidence-new-version-submit">
            {progress !== null ? "Uploading…" : "Upload version"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const DeleteEvidenceDialog = ({ item, onClose, onDone }) => {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setReason(""); setBusy(false); }, [item]);
  const submit = async () => {
    if (reason.trim().length < 3) return toast.error("Give a reason for deleting this evidence");
    setBusy(true);
    try {
      await api.delete(`/grc/evidence/${item.file_id}`, { data: { reason: reason.trim() } });
      toast.success(`Moved ${item.filename} to the recycle bin`, { duration: 8000 });
      onDone();
    } catch (e) {
      toast.error(errText(e, "Delete failed"));
      setBusy(false);
    }
  };
  return (
    <Dialog open={!!item} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="evidence-delete-confirm">
        <DialogHeader>
          <DialogTitle className="text-base">Delete evidence?</DialogTitle>
          <DialogDescription className="text-xs">
            "{item?.filename}" and all its versions move to the recycle bin and are unlinked from the control. You can restore them later.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label className="text-xs">Reason *</Label>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why is this evidence being removed?"
            className="text-xs min-h-[70px]" data-testid="evidence-delete-reason" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="evidence-delete-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-red-600 hover:bg-red-700" data-testid="evidence-delete-confirm-button">
            {busy ? "Deleting…" : "Delete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const RecycleBin = ({ assessmentId, onRestored }) => {
  const [items, setItems] = useState(null);
  const load = React.useCallback(() => {
    api.get(`/grc/${assessmentId}/evidence/deleted`).then(({ data }) => setItems(data.evidence || [])).catch(() => setItems([]));
  }, [assessmentId]);
  useEffect(load, [load]);
  const restore = async (it) => {
    try {
      await api.post(`/grc/evidence/${it.file_id}/restore`);
      toast.success(`Restored ${it.filename}`, { duration: 8000 });
      load();
      onRestored();
    } catch (e) {
      toast.error(errText(e, "Restore failed"));
    }
  };
  if (items === null) return <p className="text-xs text-slate-400">Loading recycle bin…</p>;
  if (!items.length) return <p className="py-10 text-center text-sm text-slate-500" data-testid="evidence-recycle-empty">The recycle bin is empty.</p>;
  return (
    <div className="space-y-2" data-testid="evidence-recycle-list">
      {items.map((it) => (
        <div key={it.file_id} className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2" data-testid={`evidence-recycle-row-${it.file_id}`}>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-medium text-slate-800 truncate">{it.filename} <span className="text-slate-400">v{it.version_no || 1}</span></div>
            <div className="text-[10px] text-slate-400 truncate">
              Deleted {fmtDate(it.deleted_at)} by {it.deleted_by} · "{it.delete_reason}"
            </div>
          </div>
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => restore(it)} data-testid={`evidence-restore-${it.file_id}`}>
            <RotateCcw size={12} className="mr-1" /> Restore
          </Button>
        </div>
      ))}
    </div>
  );
};
